import { useState, useRef, useEffect } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, AlertTriangle, Send, Camera, Mic, Search,
  User, ChevronRight, CheckCircle2, Clock, ArrowUpRight,
  Phone, X,
} from 'lucide-react';
import { agendaOggi } from '@/data/mockOperatoreData';

/* ═══════════════════════════════════
   MOCK DATA — CONVERSAZIONI
   ═══════════════════════════════════ */
const conversazioni = [
  {
    id: 'conv-1',
    nome: 'Case Manager - Giulia',
    avatar: 'GV',
    ruolo: 'Case Manager',
    ultimoMsg: 'Ok, ho aggiornato il PAI.',
    ora: '09:32',
    nonLetti: 2,
    online: true,
    messaggi: [
      { id: 'm1', testo: 'Buongiorno Marco, come e andata la visita alla Sig.ra Bianchi?', mittente: 'altro', ora: '08:45', nome: 'Giulia Vespri' },
      { id: 'm2', testo: 'Buongiorno! Prelievo eseguito, parametri nella norma. PA 130/80, SpO2 97%.', mittente: 'io', ora: '09:10' },
      { id: 'm3', testo: 'Perfetto, grazie. Ho notato che la lesione migliora, confermi?', mittente: 'altro', ora: '09:15', nome: 'Giulia Vespri' },
      { id: 'm4', testo: 'Si, confermo. Margini in riepitelizzazione, riduzione del 20% circa.', mittente: 'io', ora: '09:22' },
      { id: 'm5', testo: 'Ottimo! Aggiorno il PAI con la riduzione delle medicazioni a 2/settimana.', mittente: 'altro', ora: '09:28', nome: 'Giulia Vespri' },
      { id: 'm6', testo: 'Ok, ho aggiornato il PAI.', mittente: 'altro', ora: '09:32', nome: 'Giulia Vespri' },
    ],
  },
  {
    id: 'conv-2',
    nome: 'Centrale Operativa (COT)',
    avatar: 'CO',
    ruolo: 'COT Distrettuale',
    ultimoMsg: 'Ricevuto, buon turno.',
    ora: '07:50',
    nonLetti: 0,
    online: true,
    messaggi: [
      { id: 'm7', testo: 'Buongiorno a tutti. Turno mattutino: 3 accessi domiciliari programmati. Nessuna variazione.', mittente: 'altro', ora: '07:30', nome: 'COT' },
      { id: 'm8', testo: 'Ricevuto, buon turno.', mittente: 'io', ora: '07:50' },
    ],
  },
  {
    id: 'conv-3',
    nome: 'Dr. Rinaldi (MMG)',
    avatar: 'FR',
    ruolo: 'Medico di Base',
    ultimoMsg: 'Grazie per l\'aggiornamento.',
    ora: 'Ieri',
    nonLetti: 0,
    online: false,
    messaggi: [
      { id: 'm9', testo: 'Dottore, le invio i risultati del prelievo della Sig.ra Bianchi di ieri.', mittente: 'io', ora: 'Ieri 14:30' },
      { id: 'm10', testo: 'Grazie Inf. Colombo. Valori nella norma, continuiamo con il piano attuale.', mittente: 'altro', ora: 'Ieri 16:15', nome: 'Dr. Rinaldi' },
      { id: 'm11', testo: 'Grazie per l\'aggiornamento.', mittente: 'altro', ora: 'Ieri 16:16', nome: 'Dr. Rinaldi' },
    ],
  },
];

const motivazioniEscalation = [
  { id: 'esc-1', label: 'Rapido peggioramento clinico', icona: AlertTriangle, colore: 'red' },
  { id: 'esc-2', label: 'Miglioramento significativo (proposta dimissione anticipata)', icona: ArrowUpRight, colore: 'emerald' },
  { id: 'esc-3', label: 'Necessita nuovo ausilio o presidio', icona: CheckCircle2, colore: 'blue' },
  { id: 'esc-4', label: 'Problematica socio-ambientale rilevata', icona: User, colore: 'amber' },
];

/* ─── Tab Switcher ─── */
function TabSwitcher({ activeTab, onTabChange }) {
  return (
    <div className="flex gap-2 p-1 bg-muted/40 rounded-2xl border border-border/30" data-testid="tab-switcher">
      {[
        { key: 'chat', label: 'Chat di Equipe', icon: MessageCircle },
        { key: 'escalation', label: 'Richiesta Rivalutazione', icon: AlertTriangle },
      ].map((tab) => {
        const Icon = tab.icon;
        const active = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => onTabChange(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200
              ${active
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            <Icon className={`w-4 h-4 ${active && tab.key === 'escalation' ? 'text-orange-500' : ''}`} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════
   TAB 1: CHAT DI EQUIPE
   ═══════════════════════════════════ */
function ChatEquipe() {
  const [convSelezionata, setConvSelezionata] = useState(conversazioni[0]);
  const [messaggiLocali, setMessaggiLocali] = useState({});
  const [inputMsg, setInputMsg] = useState('');
  const [showContacts, setShowContacts] = useState(true);
  const chatEndRef = useRef(null);

  const allMessages = [
    ...convSelezionata.messaggi,
    ...(messaggiLocali[convSelezionata.id] || []),
  ];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length, convSelezionata.id]);

  const handleSend = () => {
    if (!inputMsg.trim()) return;
    const newMsg = {
      id: `m-new-${Date.now()}`,
      testo: inputMsg.trim(),
      mittente: 'io',
      ora: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessaggiLocali((prev) => ({
      ...prev,
      [convSelezionata.id]: [...(prev[convSelezionata.id] || []), newMsg],
    }));
    setInputMsg('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[520px]" data-testid="chat-equipe-section">
      {/* Contact List */}
      <div className={`${showContacts ? 'block' : 'hidden'} lg:block lg:w-72 shrink-0`}>
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl h-full" data-testid="contact-list">
          <CardContent className="p-3 flex flex-col h-full">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Cerca contatto..."
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-border/40 bg-background text-sm outline-none focus:border-primary/50"
                data-testid="contact-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {conversazioni.map((conv) => (
                <button
                  key={conv.id}
                  data-testid={`contact-${conv.id}`}
                  onClick={() => { setConvSelezionata(conv); setShowContacts(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-150
                    ${convSelezionata.id === conv.id
                      ? 'bg-primary/8 border border-primary/12'
                      : 'hover:bg-muted/50 border border-transparent'
                    }
                  `}
                >
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-muted/60 border border-border/30 flex items-center justify-center">
                      <span className="text-xs font-bold text-muted-foreground">{conv.avatar}</span>
                    </div>
                    {conv.online && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-card" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground truncate">{conv.nome}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-2">{conv.ora}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.ultimoMsg}</p>
                  </div>
                  {conv.nonLetti > 0 && (
                    <span className="w-5 h-5 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center shrink-0">
                      {conv.nonLetti}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chat Area */}
      <Card className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl flex-1 flex flex-col ${!showContacts ? 'block' : 'hidden lg:flex'}`} data-testid="chat-area">
        <CardContent className="p-0 flex flex-col h-full">
          {/* Chat Header */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border/40">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-8 w-8 rounded-lg"
              onClick={() => setShowContacts(true)}
              data-testid="back-to-contacts-btn"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
            </Button>
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-muted/60 border border-border/30 flex items-center justify-center">
                <span className="text-xs font-bold text-muted-foreground">{convSelezionata.avatar}</span>
              </div>
              {convSelezionata.online && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate" data-testid="chat-header-name">{convSelezionata.nome}</p>
              <p className="text-[10px] text-muted-foreground">{convSelezionata.ruolo}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-muted-foreground" data-testid="chat-call-btn">
              <Phone className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="message-list">
            {allMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.mittente === 'io' ? 'justify-end' : 'justify-start'}`}
                data-testid={`msg-${msg.id}`}
              >
                <div className={`max-w-[75%] ${msg.mittente === 'io' ? 'order-last' : ''}`}>
                  {msg.mittente !== 'io' && (
                    <p className="text-[10px] font-medium text-muted-foreground mb-1 ml-1">{msg.nome}</p>
                  )}
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed
                    ${msg.mittente === 'io'
                      ? 'bg-primary text-primary-foreground rounded-br-lg'
                      : 'bg-muted/50 text-foreground border border-border/30 rounded-bl-lg'
                    }
                  `}>
                    {msg.testo}
                  </div>
                  <p className={`text-[10px] text-muted-foreground mt-1 ${msg.mittente === 'io' ? 'text-right mr-1' : 'ml-1'}`}>
                    {msg.ora}
                  </p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-border/40 px-4 py-3">
            <div className="flex items-end gap-2">
              <button
                className="h-12 w-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
                data-testid="chat-photo-btn"
              >
                <Camera className="w-5 h-5" />
              </button>
              <button
                className="h-12 w-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
                data-testid="chat-voice-btn"
              >
                <Mic className="w-5 h-5" />
              </button>
              <div className="flex-1 relative">
                <textarea
                  value={inputMsg}
                  onChange={(e) => setInputMsg(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scrivi un messaggio..."
                  rows={1}
                  className="w-full h-12 px-4 py-3 rounded-xl border border-border/40 bg-background text-sm resize-none outline-none focus:border-primary/50 transition-colors"
                  data-testid="chat-input"
                />
              </div>
              <Button
                className="h-12 w-12 rounded-xl shrink-0"
                onClick={handleSend}
                disabled={!inputMsg.trim()}
                data-testid="chat-send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════
   TAB 2: ESCALATION / RIVALUTAZIONE
   ═══════════════════════════════════ */
function EscalationPanel() {
  const [pazienteId, setPazienteId] = useState('');
  const [motivazione, setMotivazione] = useState('');
  const [noteEsc, setNoteEsc] = useState('');
  const [inviato, setInviato] = useState(false);

  const pazientiDisponibili = agendaOggi.map((v) => ({
    id: v.id,
    nome: `${v.paziente.cognome} ${v.paziente.nome}`,
    pai: v.codicePAI,
  }));

  const pazienteSelezionato = pazientiDisponibili.find((p) => p.id === pazienteId);

  const canSubmit = pazienteId && motivazione && noteEsc.trim().length >= 5;

  const handleInvio = () => {
    setInviato(true);
  };

  const handleReset = () => {
    setInviato(false);
    setPazienteId('');
    setMotivazione('');
    setNoteEsc('');
  };

  if (inviato) {
    const motLabel = motivazioniEscalation.find((m) => m.id === motivazione)?.label || '';
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="escalation-sent-state"
      >
        <Card className="border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)] rounded-2xl">
          <CardContent className="py-12 flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 rounded-full bg-orange-500 flex items-center justify-center mb-5"
            >
              <AlertTriangle className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-xl font-bold text-foreground" data-testid="escalation-sent-title">
              Alert Inviato con Successo
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              La richiesta di rivalutazione per <strong>{pazienteSelezionato?.nome}</strong> e stata inviata al Case Manager con priorita alta.
            </p>
            <div className="mt-4 px-4 py-3 rounded-2xl bg-orange-50 dark:bg-orange-950/20 border border-orange-200/50 dark:border-orange-800/40 max-w-sm">
              <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                Motivo: {motLabel}
              </p>
            </div>
            <div className="mt-5 flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">
                Inviato alle {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <Button
              variant="outline"
              className="mt-6 rounded-xl gap-2"
              onClick={handleReset}
              data-testid="escalation-new-btn"
            >
              Nuova Richiesta
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="space-y-5" data-testid="escalation-panel">
      {/* Warning Banner */}
      <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-orange-50 dark:bg-orange-950/20 border border-orange-200/50 dark:border-orange-800/40">
        <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0" />
        <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
          Usa questo modulo per segnalare urgenze cliniche o richiedere una rivalutazione del PAI.
        </p>
      </div>

      {/* Patient Selection */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="escalation-patient-card">
        <CardContent className="p-5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
            Seleziona Paziente
          </label>
          <select
            value={pazienteId}
            onChange={(e) => setPazienteId(e.target.value)}
            className="w-full h-14 px-4 rounded-2xl border-2 border-border/40 bg-background text-sm font-medium text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all appearance-none cursor-pointer"
            data-testid="escalation-patient-select"
          >
            <option value="">— Seleziona un paziente —</option>
            {pazientiDisponibili.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome} ({p.pai})
              </option>
            ))}
          </select>
          {pazienteSelezionato && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 border border-primary/10"
            >
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{pazienteSelezionato.nome}</span>
              <Badge variant="outline" className="text-[10px] ml-auto">{pazienteSelezionato.pai}</Badge>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Motivation Selection */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="escalation-motivation-card">
        <CardContent className="p-5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-3">
            Motivazione della Rivalutazione
          </label>
          <div className="space-y-2.5">
            {motivazioniEscalation.map((m) => {
              const Icon = m.icona;
              const selected = motivazione === m.id;
              const colorMap = {
                red: { bg: 'bg-red-50 dark:bg-red-950/20', border: 'border-red-300 dark:border-red-700', text: 'text-red-700 dark:text-red-300', icon: 'text-red-500' },
                emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-300 dark:border-emerald-700', text: 'text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-500' },
                blue: { bg: 'bg-blue-50 dark:bg-blue-950/20', border: 'border-blue-300 dark:border-blue-700', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-500' },
                amber: { bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-300 dark:border-amber-700', text: 'text-amber-700 dark:text-amber-300', icon: 'text-amber-500' },
              };
              const c = colorMap[m.colore] || colorMap.amber;

              return (
                <button
                  key={m.id}
                  data-testid={`motivation-${m.id}`}
                  onClick={() => setMotivazione(m.id)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl border-2 text-left transition-all duration-200
                    ${selected
                      ? `${c.bg} ${c.border} shadow-sm`
                      : 'border-border/30 hover:border-border/60 bg-card'
                    }
                  `}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${selected ? c.bg : 'bg-muted/40'}`}>
                    <Icon className={`w-5 h-5 ${selected ? c.icon : 'text-muted-foreground'}`} />
                  </div>
                  <span className={`text-sm font-semibold ${selected ? c.text : 'text-foreground'}`}>
                    {m.label}
                  </span>
                  {selected && (
                    <CheckCircle2 className={`w-5 h-5 ml-auto shrink-0 ${c.icon}`} />
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Additional Notes */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="escalation-notes-card">
        <CardContent className="p-5">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
            Note Aggiuntive
          </label>
          <textarea
            value={noteEsc}
            onChange={(e) => setNoteEsc(e.target.value)}
            placeholder="Descrivi brevemente il problema o la motivazione della richiesta..."
            rows={4}
            className="w-full px-4 py-4 rounded-2xl border-2 border-border/40 bg-background text-sm resize-none outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all"
            data-testid="escalation-notes-textarea"
          />
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="space-y-2">
        <Button
          className="w-full h-16 rounded-2xl text-base font-bold gap-3 shadow-lg hover:shadow-xl transition-all bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-500 text-white"
          disabled={!canSubmit}
          onClick={handleInvio}
          data-testid="escalation-submit-btn"
        >
          <AlertTriangle className="w-6 h-6" />
          Invia Alert al Case Manager (Alta Priorita)
        </Button>
        {!canSubmit && (
          <p className="text-center text-xs text-muted-foreground">
            Seleziona paziente, motivazione e aggiungi una nota per procedere.
          </p>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════ */
export default function HealthWorkerCommunications() {
  const [activeTab, setActiveTab] = useState('chat');

  return (
    <AppLayout role="operatore">
      <div className="space-y-5" data-testid="hw-communications-page">
        {/* Page Header */}
        <div>
          <h1 className="text-xl font-bold text-foreground" data-testid="comms-title">Comunicazioni</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Messaggistica sicura e richieste di rivalutazione clinica
          </p>
        </div>

        {/* Tab Switcher */}
        <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'chat' ? (
            <motion.div
              key="chat"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.15 }}
            >
              <ChatEquipe />
            </motion.div>
          ) : (
            <motion.div
              key="escalation"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
            >
              <EscalationPanel />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
